import React from "react";

import {
	Checkbox,
	InfoMessage,
	SectionCard,
	Spinner,
	Toggle,
} from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import AdminService from "@/services/AdminService";
import UserService from "@/services/UserService";

import { useAuth } from "@/hooks/useAuth";

import { adminKeys, meKeys } from "@/constants/CQueryKeys";
import { UserRole } from "@/constants/CRoles";

import { TAdminFeatureFlag } from "@/types/TAdmin";

import { promiseToast } from "@/modules/index";

const ROLES = [UserRole.ADMIN, UserRole.USER];

/**
 * The features tab: what exists for whom (feature_flags). On for
 * everyone, or only for some app roles - the admin keeps a paused one.
 * A change reaches the others at their next /me; the admin's own at once.
 */
export const AdminFeatures: React.FC = () => {
	const queryClient = useQueryClient();
	const { updateUser } = useAuth();
	const { data, isLoading, error } = useQuery({
		queryKey: adminKeys.features,
		queryFn: AdminService.features,
	});

	const save = (
		flag: TAdminFeatureFlag,
		changes: Partial<Pick<TAdminFeatureFlag, "enabled" | "enabledForRoles">>
	) => {
		const prom = AdminService.setFeature(flag.code, {
			enabled: changes.enabled ?? flag.enabled,
			enabledForRoles: changes.enabledForRoles ?? flag.enabledForRoles,
		})
			.then(() =>
				queryClient.invalidateQueries({ queryKey: adminKeys.features })
			)
			.then(() => UserService.fetchMe())
			.then((me) => {
				queryClient.setQueryData(meKeys.all, me);
				updateUser(me);
			});
		promiseToast(prom);
	};

	if (isLoading) return <Spinner className="mx-auto my-6" />;
	if (error || !data) return null;

	return (
		<div className="flex max-w-3xl flex-col gap-4">
			<InfoMessage color="info">{t("admin.features.help")}</InfoMessage>
			{data.map((flag) => (
				<SectionCard
					key={flag.code}
					title={flag.name}
					data-testid={`feature-${flag.code}`}
				>
					<div className="flex flex-col gap-4">
						{flag.description && (
							<p className="text-sm text-neutral-600">
								{flag.description}
							</p>
						)}
						<Toggle
							label={t("admin.features.everyone")}
							checked={flag.enabled}
							onChange={(enabled) => save(flag, { enabled })}
						/>
						<div className="flex flex-wrap items-center gap-4">
							<span className="text-sm text-neutral-500">
								{t("admin.features.roles")}
							</span>
							{ROLES.map((role) => (
								<Checkbox
									key={role}
									label={t(`admin.features.${role}`)}
									checked={flag.enabledForRoles.includes(
										role
									)}
									disabled={flag.enabled}
									onChange={(checked) =>
										save(flag, {
											enabledForRoles: checked
												? [
														...flag.enabledForRoles,
														role,
													]
												: flag.enabledForRoles.filter(
														(r) => r !== role
													),
										})
									}
								/>
							))}
						</div>
						<span className="text-xs text-neutral-400">
							{t("admin.features.updated", {
								date: format(
									new Date(flag.updatedAt),
									"dd/MM/yyyy HH:mm"
								),
							})}
						</span>
					</div>
				</SectionCard>
			))}
		</div>
	);
};
